import {useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {GroupHeader} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {STATUS_TONE, planAccionHref} from '@/components/accion-ligada-link'
import {CrearMejoraDialog} from '@/components/crear-mejora-dialog'
import {EntrevistaTranscriptDialog} from '@/components/entrevista-transcript-dialog'
import {ControlDimensionEditor} from '@/components/ipevr/control-combobox'
import {DescartarFilaDialog, DesasociarProcedenciaDialog} from '@/components/ipevr/descarte-dialogs'
import {MedidasSection} from '@/components/ipevr/medidas-section'
import {NormasDeLaFila} from '@/components/vinculo-norma-riesgo'
import {
  CLASIFICACION_TONE,
  CONTROL_TIPOS,
  INTERPRETACION_TONE,
  gateBlockedCopy,
  ndToValorKey,
  useConfirmarFila,
  useCrearMejora,
  useDesasociarProcedencia,
  useDescartarFila,
  useFilaNovedades,
  useFilaOrigen,
  useIncorporarNovedades,
  useMantenerFila,
  type Fila,
  type Matriz,
} from '@/hooks/sgsst/riesgos/use-ipevr'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {translateApiError} from '@/lib/errors'

function NovedadesDiffPanel({
  businessId,
  matriz,
  fila,
  canEdit,
  onDone,
  contenedor,
}: {
  businessId: string
  matriz: Matriz
  fila: Fila
  canEdit: boolean
  onDone: () => void
  /** Sin él, el popover del `PorQue` se porta a `document.body`, fuera del focus scope del modal. */
  contenedor: HTMLElement | null
}) {
  const {t} = useTranslation()
  const {data: diff, isLoading} = useFilaNovedades(businessId, fila.id, fila.novedades_pendientes)
  const incorporar = useIncorporarNovedades(businessId)
  const mantener = useMantenerFila(businessId)
  const [error, setError] = useState<string | null>(null)

  const canIncorporar = matriz.can_mark_vigente || matriz.mark_vigente_dev_override

  if (isLoading) return <Skeleton className="h-16 w-full" data-testid="fila-novedades-loading" />
  if (!diff || (diff.num_expuestos === null && diff.nivel_deficiencia === null)) return null

  const nivelPropuesto = diff.nivel_deficiencia
    ? [
        gtc45Label(matriz.gtc45.nd, ndToValorKey(diff.nivel_deficiencia.propuesto_nd)),
        diff.nivel_deficiencia.propuesto_ne ?? '—',
        diff.nivel_deficiencia.propuesto_nc ?? '—',
      ].join(' · ')
    : null

  return (
    <div className="space-y-3 border-t border-border pt-3" data-testid="fila-novedades-diff">
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('ipevr.novedades.diffTitle')}
      </p>
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className="border-b border-border pb-1.5 text-left font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('ipevr.novedades.campo')}
            </th>
            <th className="border-b border-border pb-1.5 text-left font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('ipevr.novedades.confirmado')}
            </th>
            <th className="border-b border-border pb-1.5 text-left font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('ipevr.novedades.propuesto')}
            </th>
          </tr>
        </thead>
        <tbody>
          {diff.num_expuestos && (
            <tr data-testid="fila-novedades-row-num-expuestos">
              <td className="border-b border-border py-2 pr-3 text-foreground">{t('ipevr.novedades.numExpuestos')}</td>
              <td className="border-b border-border py-2 pr-3 font-mono text-muted-foreground">
                {diff.num_expuestos.actual}
              </td>
              <td className="border-b border-border py-2">
                {/* `neutral` y no verde: nadie confirmó este número y el verde empujaría
                    a aceptar la sugerencia (U2(b)). */}
                <Tag tone="neutral">{diff.num_expuestos.propuesto}</Tag>
              </td>
            </tr>
          )}
          {nivelPropuesto && (
            <tr data-testid="fila-novedades-row-nivel-deficiencia">
              <td className="py-2 pr-3 text-foreground">{t('ipevr.novedades.nivelDeficiencia')}</td>
              <td className="py-2 pr-3 text-muted-foreground">{t('ipevr.novedades.sinDato')}</td>
              <td className="py-2">
                <Tag tone="neutral">{nivelPropuesto}</Tag>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* T8: el banner de la página queda detrás de este modal, así que el motivo
          del bloqueo y su salida se repiten acá. */}
      {canEdit && !canIncorporar && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs text-destructive" data-testid="fila-novedades-blocked">
            {t('ipevr.novedades.blocked')}
          </p>
          <PorQue
            label={t('ipevr.porQue')}
            ariaLabel={t('ipevr.tipLabel.gate')}
            testId="fila-novedades-blocked-porque"
            contenidoTestId="fila-novedades-blocked-porque-contenido"
            contenedor={contenedor}>
            {gateBlockedCopy(t, matriz).porque}
          </PorQue>
        </div>
      )}
      {error && (
        <p className="text-xs text-destructive" data-testid="fila-novedades-error">
          {error}
        </p>
      )}

      {canEdit && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={mantener.isPending}
            onClick={() =>
              mantener.mutate(fila.id, {
                onSuccess: onDone,
                onError: (err) => setError(translateApiError(err) || t('ipevr.novedades.mantenerError')),
              })
            }
            data-testid="fila-novedades-mantener">
            {t('ipevr.novedades.mantener')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={incorporar.isPending || !canIncorporar}
            onClick={() =>
              incorporar.mutate(fila.id, {
                onSuccess: onDone,
                onError: (err) => setError(translateApiError(err) || t('ipevr.novedades.incorporarError')),
              })
            }
            data-testid="fila-novedades-incorporar">
            {t('ipevr.novedades.incorporar')}
          </Button>
        </div>
      )}
    </div>
  )
}

function Field({label, value}: {label: string; value: ReactNode}) {
  return (
    <div className="space-y-1">
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">{label}</p>
      <div className="text-sm text-foreground">{value ?? <span className="text-muted-foreground">—</span>}</div>
    </div>
  )
}

function gtc45Label(options: {valor: number | null; etiqueta_corta: string}[], key: string | null): string | null {
  if (key === null) return null
  const match = options.find((o) => (o.valor === null ? 'bajo' : String(o.valor)) === key)
  return match?.etiqueta_corta ?? key
}

export function FilaDetailsDialog({
  businessId,
  matriz,
  fila,
  canEdit,
  onClose,
  onEdit,
}: {
  businessId: string
  matriz: Matriz
  fila: Fila | null
  canEdit: boolean
  onClose: () => void
  onEdit: (fila: Fila) => void
}) {
  const {t} = useTranslation()
  const confirmar = useConfirmarFila(businessId)
  const rechazar = useDescartarFila(businessId)
  const desasociar = useDesasociarProcedencia(businessId)
  const crearMejora = useCrearMejora(businessId)
  const {data: origen, isLoading: origenLoading} = useFilaOrigen(businessId, fila?.id)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [descarteError, setDescarteError] = useState<string | null>(null)
  const [confirmingRechazo, setConfirmingRechazo] = useState(false)
  const [desasociando, setDesasociando] = useState<{hallazgoId: string; name: string} | null>(null)
  const [transcriptOf, setTranscriptOf] = useState<{id: string; name: string; last_name: string} | null>(null)
  const [creandoMejora, setCreandoMejora] = useState(false)
  // Ref de callback y no `useRef` + efecto: Radix monta el contenido del diálogo en
  // un commit posterior al primer efecto.
  const [dialogNode, setDialogNode] = useState<HTMLElement | null>(null)
  const {data: business} = useBusiness(businessId)

  if (!fila) return null

  const sugerido = fila.confirmado_en === null
  const noAceptable = fila.interpretacion === 'I' || fila.interpretacion === 'II'
  const area = matriz.areas.find((a) => a.id === fila.area_id)?.name ?? null

  return (
    <>
      <Dialog open onOpenChange={(next) => !next && onClose()}>
        <DialogContent
          ref={setDialogNode}
          className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden"
          data-testid="ipevr-fila-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              {t('ipevr.details.title')}
              {sugerido ? (
                <Tag tone="amber" data-testid="fila-details-unconfirmed">
                  {t('ipevr.grid.unconfirmed')}
                </Tag>
              ) : (
                <Tag tone="green" data-testid="fila-details-confirmed">
                  {t('ipevr.grid.confirmed')}
                </Tag>
              )}
              {!sugerido && fila.confirmado_por && (
                <span
                  className="font-mono text-[11px] font-normal text-muted-foreground"
                  data-testid="fila-details-confirmed-by">
                  {t('ipevr.details.confirmadaPor', {
                    name: `${fila.confirmado_por.name} ${fila.confirmado_por.last_name}`,
                  })}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {fila.novedades_pendientes && (
            <div className="notice-amber rounded-lg px-3.5 py-2.5 text-xs" data-testid="fila-details-novedades-banner">
              {t('ipevr.novedades.banner')}
            </div>
          )}

          {fila.novedades_pendientes && (
            <NovedadesDiffPanel
              businessId={businessId}
              matriz={matriz}
              fila={fila}
              canEdit={canEdit}
              onDone={onClose}
              contenedor={dialogNode}
            />
          )}

          <Tabs defaultValue="contexto" className="flex min-h-0 min-w-0 flex-1 flex-col">
            <TabsList data-testid="fila-details-tabs">
              <TabsTrigger value="contexto" data-testid="fila-details-tab-contexto">
                {t('ipevr.details.tabs.contexto')}
              </TabsTrigger>
              <TabsTrigger value="peligro" data-testid="fila-details-tab-peligro">
                {t('ipevr.details.tabs.peligro')}
              </TabsTrigger>
              <TabsTrigger value="controles" data-testid="fila-details-tab-controles">
                {t('ipevr.details.tabs.controles')}
              </TabsTrigger>
              <TabsTrigger value="evaluacion" data-testid="fila-details-tab-evaluacion">
                {t('ipevr.details.tabs.evaluacion')}
              </TabsTrigger>
            </TabsList>

            <div className="min-h-0 flex-1 overflow-y-auto py-4">
              <TabsContent value="contexto" className="space-y-5">
                <section className="space-y-3">
                  <GroupHeader
                    label={t('ipevr.groups.contexto')}
                    help={t('ipevr.groups.contextoHelp')}
                    testId="fila-details-help-contexto"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t('ipevr.grid.proceso')} value={fila.proceso} />
                    <Field label={t('ipevr.grid.zona')} value={fila.zona} />
                    <Field label={t('ipevr.grid.actividad')} value={fila.actividad} />
                    <Field label={t('ipevr.grid.tarea')} value={fila.tarea} />
                    <Field
                      label={t('ipevr.grid.rutinaria')}
                      value={fila.rutinaria ? t('ipevr.common.yes') : t('ipevr.common.no')}
                    />
                  </div>
                </section>

                <section
                  className="space-y-2 rounded-lg border border-border bg-secondary/40 p-3"
                  data-testid="fila-origen">
                  <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                    {t('ipevr.details.origen')}
                  </p>
                  {origenLoading ? (
                    <Skeleton className="h-8 w-full" />
                  ) : origen && origen.contributors.length > 0 ? (
                    <div className="space-y-3" data-testid="fila-origen-entrevista">
                      {origen.contributors.length > 1 && (
                        <p className="text-sm text-foreground">
                          {t('ipevr.details.origenEntrevistaMultiple', {count: origen.contributors.length})}
                        </p>
                      )}
                      {origen.contributors.map((contributor) => (
                        <div
                          key={`${contributor.employee_id}-${contributor.turno_id}`}
                          className="space-y-2 border-l-2 border-primary/40 pl-3"
                          data-testid="fila-origen-contribuyente">
                          <p className="text-sm text-foreground">
                            {t('ipevr.details.origenEntrevista', {
                              name: `${contributor.employee_name} ${contributor.employee_last_name}`,
                            })}
                          </p>
                          {contributor.pregunta && (
                            <div className="space-y-0.5" data-testid="fila-origen-pregunta">
                              <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                                {t('ipevr.details.preguntaLabel')}
                              </p>
                              <p className="text-sm text-foreground">{contributor.pregunta}</p>
                            </div>
                          )}
                          <blockquote className="text-sm text-muted-foreground italic">
                            «{contributor.quote}»
                          </blockquote>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              data-testid="fila-origen-ver-entrevista"
                              onClick={() =>
                                setTranscriptOf({
                                  id: contributor.employee_id,
                                  name: contributor.employee_name,
                                  last_name: contributor.employee_last_name,
                                })
                              }>
                              {t('ipevr.details.verEntrevista')}
                            </Button>
                            {canEdit && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                data-testid="fila-origen-desasociar"
                                onClick={() =>
                                  setDesasociando({
                                    hallazgoId: contributor.hallazgo_id,
                                    name: `${contributor.employee_name} ${contributor.employee_last_name}`,
                                  })
                                }>
                                {t('ipevr.details.desasociar')}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : origen?.marker ? (
                    <p className="text-sm text-muted-foreground" data-testid="fila-origen-marker">
                      {origen.marker}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground" data-testid="fila-origen-marker">
                      {t('ipevr.details.origenManual')}
                    </p>
                  )}
                </section>
              </TabsContent>

              <TabsContent value="peligro" className="space-y-3">
                <GroupHeader
                  label={t('ipevr.groups.peligro')}
                  help={t('ipevr.groups.peligroHelp')}
                  testId="fila-details-help-peligro"
                />
                <Field
                  label={t('ipevr.grid.clasificacion')}
                  value={
                    <Tag tone={CLASIFICACION_TONE[fila.clasificacion]}>
                      {t(`ipevr.clasificacion.${fila.clasificacion}`)}
                    </Tag>
                  }
                />
                <Field label={t('ipevr.grid.descripcion')} value={fila.descripcion} />
                <Field label={t('ipevr.grid.efectosPosibles')} value={fila.efectos_posibles} />
              </TabsContent>

              <TabsContent value="controles" className="space-y-5">
                <section className="space-y-4">
                  <GroupHeader
                    label={t('ipevr.groups.controles')}
                    help={t('ipevr.groups.controlesHelp')}
                    testId="fila-details-help-controles"
                  />
                  {CONTROL_TIPOS.map((tipo) => (
                    <ControlDimensionEditor
                      key={tipo}
                      businessId={businessId}
                      fila={fila}
                      tipo={tipo}
                      canEdit={canEdit}
                    />
                  ))}
                </section>

                <MedidasSection businessId={businessId} fila={fila} canEdit={canEdit} />

                <section className="space-y-3">
                  <GroupHeader
                    label={t('ipevr.groups.criterios')}
                    help={t('ipevr.groups.criteriosHelp')}
                    testId="fila-details-help-criterios"
                  />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Field label={t('ipevr.grid.area')} value={area} />
                    <Field label={t('ipevr.grid.numExpuestos')} value={fila.num_expuestos} />
                    <Field label={t('ipevr.grid.peorConsecuencia')} value={fila.peor_consecuencia} />
                  </div>
                  <span className="flex items-center gap-1.5">
                    <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                      {t('ipevr.grid.requisitoLegal')}
                    </p>
                    <span className="text-sm text-foreground">
                      {fila.requisito_legal ? t('ipevr.common.yes') : t('ipevr.common.no')}
                    </span>
                  </span>
                  <NormasDeLaFila businessId={businessId} fila={fila} canEdit={canEdit} />
                </section>
              </TabsContent>

              <TabsContent value="evaluacion" className="space-y-5">
                <section className="space-y-3">
                  <GroupHeader
                    label={t('ipevr.groups.evaluacion')}
                    help={t('ipevr.groups.evaluacionHelp')}
                    testId="fila-details-help-evaluacion"
                  />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Field
                      label={`${t('ipevr.grid.ndPlain')} (ND)`}
                      value={fila.nd === null ? null : gtc45Label(matriz.gtc45.nd, ndToValorKey(fila.nd))}
                    />
                    <Field
                      label={`${t('ipevr.grid.nePlain')} (NE)`}
                      value={gtc45Label(matriz.gtc45.ne, fila.ne === null ? null : String(fila.ne))}
                    />
                    <Field
                      label={`${t('ipevr.grid.ncPlain')} (NC)`}
                      value={gtc45Label(matriz.gtc45.nc, fila.nc === null ? null : String(fila.nc))}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                    <Field label={t('ipevr.sort.np')} value={fila.np} />
                    <Field label={t('ipevr.sort.nr')} value={fila.nr} />
                    <Field
                      label={t('ipevr.grid.interpretacion')}
                      value={
                        fila.interpretacion ? (
                          <Tag
                            tone={INTERPRETACION_TONE[fila.interpretacion]}
                            data-testid="fila-details-interpretacion">
                            {fila.interpretacion}
                          </Tag>
                        ) : null
                      }
                    />
                    <Field label={t('ipevr.grid.aceptabilidad')} value={fila.aceptabilidad} />
                  </div>
                </section>
              </TabsContent>
            </div>
          </Tabs>

          <div className="space-y-2 border-t border-border pt-3">
            {/* Visible y no en `title=`: un title nativo no abre por teclado ni por
                toque (T8/T11). */}
            {canEdit && sugerido && !matriz.can_mark_vigente && (
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs text-muted-foreground" data-testid="fila-details-confirmar-gate">
                  {gateBlockedCopy(t, matriz).tooltip}
                </p>
                <PorQue
                  label={t('ipevr.porQue')}
                  ariaLabel={t('ipevr.tipLabel.gate')}
                  testId="fila-details-confirmar-gate-porque"
                  contenidoTestId="fila-details-confirmar-gate-porque-contenido"
                  contenedor={dialogNode}>
                  {gateBlockedCopy(t, matriz).porque}
                </PorQue>
              </div>
            )}
            {confirmError && (
              <p className="text-xs text-destructive" data-testid="fila-details-confirm-error">
                {confirmError}
              </p>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {canEdit &&
                  (sugerido ? (
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={rechazar.isPending}
                      onClick={() => setConfirmingRechazo(true)}
                      data-testid="fila-details-rechazar">
                      {t('ipevr.grid.sugerido.rechazar')}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={rechazar.isPending}
                      onClick={() => setConfirmingRechazo(true)}
                      aria-label={t('ipevr.grid.deleteFila')}
                      data-testid="fila-details-delete">
                      {t('ipevr.grid.deleteFila')}
                    </Button>
                  ))}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                {canEdit &&
                  !sugerido &&
                  noAceptable &&
                  (fila.acciones.length > 0 ? (
                    <Link
                      to={planAccionHref(businessId, fila.acciones[0].id)}
                      data-testid="fila-details-plan-count"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
                      {t('mejora.verAccion')}
                      <Tag tone={STATUS_TONE[fila.acciones[0].estado]} data-testid="fila-details-accion-estado">
                        {t(`plan.status.${fila.acciones[0].estado}`)}
                      </Tag>
                    </Link>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreandoMejora(true)}
                      data-testid="fila-details-crear-mejora">
                      {t('ipevr.grid.crearMejora')}
                    </Button>
                  ))}
                <Button type="button" size="sm" variant="outline" onClick={onClose}>
                  {canEdit ? t('common.cancel') : t('common.close')}
                </Button>
                {canEdit && sugerido && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(fila)}
                    data-testid="fila-details-edit">
                    {t('ipevr.editFila.open')}
                  </Button>
                )}
                {canEdit && sugerido && (
                  <Button
                    size="sm"
                    disabled={confirmar.isPending || (!matriz.can_mark_vigente && !matriz.mark_vigente_dev_override)}
                    onClick={() =>
                      confirmar.mutate(fila.id, {
                        onSuccess: onClose,
                        onError: (err) => setConfirmError(translateApiError(err)),
                      })
                    }
                    data-testid="fila-details-confirmar">
                    {t('ipevr.grid.sugerido.confirmar')}
                  </Button>
                )}
                {canEdit && !sugerido && (
                  <Button type="button" size="sm" onClick={() => onEdit(fila)} data-testid="fila-details-edit">
                    {t('ipevr.editFila.open')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <DescartarFilaDialog
        open={confirmingRechazo}
        onOpenChange={(next) => {
          setConfirmingRechazo(next)
          if (!next) setDescarteError(null)
        }}
        sugerido={sugerido}
        pending={rechazar.isPending}
        error={descarteError}
        onConfirm={(motivo) =>
          rechazar.mutate(
            {filaId: fila.id, motivo},
            {
              onSuccess: () => {
                setConfirmingRechazo(false)
                onClose()
              },
              onError: (err) => setDescarteError(translateApiError(err) || t('ipevr.descarte.error')),
            }
          )
        }
      />
      {desasociando && (
        <DesasociarProcedenciaDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setDesasociando(null)
              setDescarteError(null)
            }
          }}
          personaName={desasociando.name}
          esUltima={(origen?.contributors.length ?? 0) <= 1}
          pending={desasociar.isPending || rechazar.isPending}
          error={descarteError}
          onDesasociar={(motivo) =>
            desasociar.mutate(
              {
                filaId: fila.id,
                hallazgoId: desasociando.hallazgoId,
                motivo,
                conservarSinProcedencia: (origen?.contributors.length ?? 0) <= 1,
              },
              {
                onSuccess: () => setDesasociando(null),
                onError: (err) => setDescarteError(translateApiError(err) || t('ipevr.descarte.error')),
              }
            )
          }
          onDescartarFila={(motivo) =>
            rechazar.mutate(
              {filaId: fila.id, motivo},
              {
                onSuccess: () => {
                  setDesasociando(null)
                  onClose()
                },
                onError: (err) => setDescarteError(translateApiError(err) || t('ipevr.descarte.error')),
              }
            )
          }
        />
      )}
      <CrearMejoraDialog
        businessId={businessId}
        open={creandoMejora}
        onOpenChange={setCreandoMejora}
        origen={{
          etiqueta: t('mejora.origen.fila'),
          titulo: fila.descripcion,
          detalle: t(`ipevr.clasificacion.${fila.clasificacion}`),
          testId: 'crear-mejora-dialog',
        }}
        descripcion={fila.descripcion}
        responsableId={business?.legal_representative_id ?? null}
        onCrear={(editado) => crearMejora.mutateAsync({filaId: fila.id, editado})}
      />
      <EntrevistaTranscriptDialog
        businessId={businessId}
        employee={transcriptOf}
        onClose={() => setTranscriptOf(null)}
      />
    </>
  )
}
